import { envConfig } from '#constants/envConfig';
import { SENTRY_DSN as DSN } from './dsnTable';

/**
 * Routes Sentry/GlitchTip events to the right project by platform × environment.
 *
 * One project per (platform, env) on sentry.teamplay.gg (org "teamplay"), instead of
 * everything landing in the single `teamplay-frontend` project:
 *   web-dev / web-prod        — the browser build
 *   android-dev / android-prod — the Capacitor shell on Android
 *   ios-dev / ios-prod         — the Capacitor shell on iOS
 *
 * The table itself lives in `dsnTable.ts`, free of imports, so the build can read it too.
 */

// Reuse the existing, safely-computed dev/prod signal. Do NOT re-derive this by calling
// `.includes()` on an inlined env var — the minifier miscompiled that into `(void 0)(...)`
// and 500'd every SSR route (see envConfig.SENTRY_ENVIRONMENT).
const isDev = (): boolean => envConfig.SENTRY_ENVIRONMENT === 'development';

// Capacitor exposes a global in the native WebView; read the platform synchronously.
// Only ever reached in native builds (guarded by IS_NATIVE), so it tree-shakes out of web.
// Всё, что не iOS, - Android: так выбирается и DSN ниже, и метка платформы, чтобы событие не
// лежало в проекте Android с меткой web.
const nativePlatform = (): 'ios' | 'android' =>
  (globalThis as { Capacitor?: { getPlatform?: () => string } }).Capacitor?.getPlatform?.() ===
  'ios'
    ? 'ios'
    : 'android';

export const resolveSentryDsn = (): string => {
  const dev = isDev();
  if (envConfig.IS_NATIVE) {
    if (nativePlatform() === 'ios') {
      return dev ? DSN.iosDev : DSN.iosProd;
    }
    return dev ? DSN.androidDev : DSN.androidProd;
  }
  return dev ? DSN.webDev : DSN.webProd;
};

/** Синхронная метка нужна и самым ранним событиям, до ответа App.getInfo(). */
export const resolveSentryPlatform = (): 'ios' | 'android' | 'web' =>
  envConfig.IS_NATIVE ? nativePlatform() : 'web';
